import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Database, Package, Home, Tag, ShoppingBag, CheckCircle,
  AlertCircle, Loader, RefreshCw, Play
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoSeeder() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [force, setForce] = useState(false);

  const handleSeed = async () => {
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem("it_token");
      const res = await axios.post(
        `${API}/admin/seed-demo?force=${force}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult({ success: true, data: res.data });
      toast.success("Demo data seeded successfully!");
    } catch (err) {
      setResult({ success: false, error: err?.response?.data?.detail || "Seed failed" });
      toast.error("Seeding failed. Check the error below.");
    } finally {
      setLoading(false);
    }
  };

  const stats = result?.data?.results;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black text-slate-900 flex items-center gap-2">
          <Database className="w-5 h-5 text-orange-500" />
          Demo Data Seeder
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Populate your store with realistic dummy products, orders, and homepage content for testing and demonstration.
        </p>
      </div>

      {/* What will be seeded */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-sm font-bold text-slate-700 mb-4">What gets seeded:</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Package,    label: "Products",        count: "32",  desc: "Across 8 categories", color: "bg-blue-50 text-blue-700 border-blue-200" },
            { icon: ShoppingBag,label: "Orders",          count: "20",  desc: "Various statuses",    color: "bg-green-50 text-green-700 border-green-200" },
            { icon: Home,       label: "Homepage Config", count: "Full",desc: "Hero + sections",     color: "bg-purple-50 text-purple-700 border-purple-200" },
            { icon: Tag,        label: "Category Banners",count: "8",   desc: "With desktop+mobile", color: "bg-orange-50 text-orange-700 border-orange-200" },
          ].map(({ icon: Icon, label, count, desc, color }) => (
            <div key={label} className={`border rounded-xl p-3 ${color}`}>
              <Icon className="w-5 h-5 mb-2" />
              <p className="text-lg font-black">{count}</p>
              <p className="text-xs font-bold">{label}</p>
              <p className="text-[11px] mt-0.5 opacity-70">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Categories covered */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Product Categories Covered</p>
        <div className="flex flex-wrap gap-2">
          {["Electronics","Home & Living","Kitchen","Personal Care","Sports & Fitness","Fashion","Beauty","Innerwear"].map(c => (
            <span key={c} className="text-xs bg-slate-100 text-slate-700 font-semibold px-3 py-1 rounded-full">{c}</span>
          ))}
        </div>
      </div>

      {/* Options + CTA */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={e => setForce(e.target.checked)}
              className="w-4 h-4 accent-orange-500 rounded"
              data-testid="seed-force-checkbox"
            />
            <span className="text-sm font-semibold text-slate-700">Force re-seed (overwrites existing demo data)</span>
          </label>
        </div>
        <p className="text-xs text-slate-400">
          Without force mode, existing products (by SKU) are skipped. Orders are only added if fewer than 15 demo orders exist.
        </p>
        <button
          onClick={handleSeed}
          disabled={loading}
          data-testid="run-seed-btn"
          className="flex items-center gap-2.5 px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold rounded-xl transition-colors text-sm shadow-md hover:shadow-lg"
        >
          {loading ? (
            <><Loader className="w-4 h-4 animate-spin" /> Seeding Data…</>
          ) : (
            <><Play className="w-4 h-4" /> Run Demo Seed</>
          )}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={`border rounded-xl p-5 ${result.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            {result.success
              ? <CheckCircle className="w-5 h-5 text-green-600" />
              : <AlertCircle className="w-5 h-5 text-red-600" />
            }
            <p className={`text-sm font-bold ${result.success ? "text-green-800" : "text-red-800"}`}>
              {result.success ? "Seeding completed successfully!" : "Seeding failed"}
            </p>
          </div>
          {result.success && stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(stats).map(([key, val]) => (
                <div key={key} className="bg-white rounded-lg px-3 py-2 border border-green-200">
                  <p className="text-xs text-slate-500 capitalize">{key.replace("_", " ")}</p>
                  <p className="text-base font-black text-green-700">{String(val)}</p>
                </div>
              ))}
            </div>
          )}
          {!result.success && (
            <p className="text-sm text-red-700">{result.error}</p>
          )}
          {result.success && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: "Visit Homepage", url: "/" },
                { label: "Browse Category", url: "/c/electronics" },
                { label: "View Orders", url: "/admin/orders" },
              ].map(link => (
                <a
                  key={link.label}
                  href={link.url}
                  className="text-center text-xs font-semibold text-green-700 bg-white border border-green-300 hover:bg-green-100 rounded-lg px-3 py-2 transition-colors"
                >
                  {link.label} →
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
